import React, { FC, ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ReactComponent as LogoIcon } from '@blink/assets/src/icons/portalLogo.svg';

import { useHeaderStyle } from './Header.style';

import { cn } from '@/shared/lib/tailwind-merge';
import { PATHS } from '@/routes/paths';
import { Button } from '@/shared/ui';

type Content = {
    text: string;
    value: string;
    children?: string[];
};

type MenuHeaderProps = {
    contentList: Content[];
    banner?: ReactNode;
    children?: ReactNode;
    rightSide?: ReactNode;
};

const NavigationMenu: FC<{ contentList: Content[]; onNavigate: (path: string) => void }> = ({
    contentList,
    onNavigate,
}) => {
    const location = useLocation();

    return (
        <div className='flex items-center'>
            {contentList.map((item, key) => {
                const isActive =
                    (item.value !== '/' && location.pathname.startsWith(item.value)) ||
                    item.value === location.pathname ||
                    item.children?.includes(location.pathname);

                return (
                    <Button
                        key={key}
                        onClick={() => onNavigate(item.value)}
                        variant={Button.types.Ghost}
                        size={Button.sizes.Small}
                        name={`${item.text}-menu`}
                        className={cn(
                            'hover:text-content-accent hover:bg-transparent',
                            isActive ? 'text-content-accent' : '',
                        )}
                    >
                        {item.text}
                    </Button>
                );
            })}
        </div>
    );
};

export const Header: FC<MenuHeaderProps> = ({ contentList, children, rightSide }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const classes = useHeaderStyle();

    if (location.pathname.includes('wallet-connect')) {
        return null;
    }

    const handleClick = (path: string) => {
        const basePath = path.endsWith('/') ? path.slice(0, -1) : path;
        const route =
            path !== PATHS.VAULTS &&
            (location.pathname === PATHS.ORDER_BOOK_BORROW || location.pathname === PATHS.BORROW)
                ? `${basePath}/borrow`
                : basePath;
        navigate(route, { state: { referrer: location.pathname } });
    };

    return (
        <>
            <header className='flex items-center justify-between h-[72px]'>
                <div className='flex items-center'>
                    <Link
                        data-testid='header-logo'
                        to={PATHS.HOME}
                        className={cn('flex items-center mr-7', classes.logoLink)}
                    >
                        <LogoIcon className='pr-7' />
                    </Link>
                    <NavigationMenu contentList={contentList} onNavigate={handleClick} />
                    {children}
                </div>
                <div className='flex items-center gap-4'>{rightSide}</div>
            </header>
        </>
    );
};
